import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'what-it-is', title: '1. What a voice line is' },
  { id: 'tiers', title: '2. Tiers, minutes & numbers' },
  { id: 'setup', title: '3. Setting one up' },
  { id: 'settings', title: '4. Settings that matter' },
  { id: 'appointments', title: '5. Appointments' },
  { id: 'recording', title: '6. Recording & consent' },
  { id: 'calls', title: '7. Reviewing calls' },
  { id: 'troubleshooting', title: '8. When something is wrong' },
];

const VoiceLinesPage = () => {
  return (
    <>
      <NextSeo
        title="Voice Lines & Appointments"
        description="Give a Chat.co bot a real phone number that answers calls using the same knowledge base, takes messages, and books appointments."
        canonical="https://chat.co/docs/voice-lines"
        openGraph={{
          url: 'https://chat.co/docs/voice-lines',
          title: 'Voice Lines & Appointments | Chat.co',
          description:
            'Give a bot a real phone number that answers calls using the same knowledge base, takes messages, and books appointments.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Deploy',
            tags: ['voice', 'phone', 'appointments', 'call handling'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Deploy &amp; Connect"
        categoryColor="text-purple-700"
        title="Voice lines &amp; appointments"
        description="A real phone number, answered by the same agent that runs your chat — with the same knowledge base behind it."
        readingTime="10 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Channel integrations',
          href: PageLink.docsChannelIntegrations,
        }}
        nextArticle={{
          title: 'MCP agent setup',
          href: PageLink.docsMcpAgentSetup,
        }}
        keyTakeaways={[
          'A voice line attaches a phone number to an existing bot — same knowledge, spoken',
          'Three tiers differ only in included minutes; phone numbers are billed separately',
          'Overage is opt-in — by default a line hard-stops at its cap rather than billing on',
          'Appointment booking requires a timezone to be set, or the booking tool stays off',
          'Recording disclosure always plays; saving the audio additionally requires caller consent',
        ]}>
        <p className="lead">
          People still phone. A voice line puts your agent on the other end of that call — answering
          from the same knowledge base your chat widget uses, taking a message when it cannot help,
          and booking appointments when that is the point of the call.
        </p>

        <section id="what-it-is" className="scroll-mt-28">
          <h2>1. What a voice line is</h2>
          <p>
            A phone number provisioned to one of your bots. When someone dials it, the bot answers
            and holds a spoken conversation in real time. It is not a phone tree and not a voicemail
            box — it is the same agent, with the same knowledge, talking.
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:book" title="Answers from your knowledge base">
              Whatever the chat bot knows, the voice bot knows. One corpus, two channels.
            </DocsCard>
            <DocsCard icon="tabler:message-2" title="Takes messages">
              When it cannot help, it captures a message — which lands in{' '}
              <Link href={PageLink.docsLeadCapture}>Leads</Link>, tagged to the call.
            </DocsCard>
            <DocsCard icon="tabler:calendar-plus" title="Books appointments">
              Where enabled, the caller can book a slot during the call.
            </DocsCard>
          </DocsCardGrid>
        </section>

        <section id="tiers" className="scroll-mt-28">
          <h2>2. Tiers, minutes &amp; numbers</h2>
          <p>
            New Chat + Voice bundles combine a Chat.co workspace, VoiceLine and one phone number in
            one subscription. Starter workspace bundles cost $69, $149 or $299 per month. A higher
            Chat.co workspace plan is an optional upgrade; it does not force a higher voice tier.
          </p>

          <DocsTable
            headers={['Tier', 'Price / month', 'Included minutes']}
            rows={[
              [<strong key="s">Starter + VoiceLine Starter</strong>, '$69', '200'],
              [<strong key="g">Starter + VoiceLine Growth</strong>, '$149', '750'],
              [<strong key="b">Starter + VoiceLine Scale</strong>, '$299', '2,000'],
            ]}
          />

          <p>
            Every self-serve bundle starts with a <strong>14-day trial</strong>, a payment method
            required, 200 trial voice minutes and one number. Yearly Starter workspace bundles cost
            $788, $1,748 or $3,548 per year after the trial; voice minutes still refresh monthly.
            Existing standalone VoiceLine subscriptions keep their agreed price and number charges.
            Bundles include one number; contact support for additional-line requirements.
          </p>

          <h3>Overage is off by default</h3>
          <p>
            This is the setting most worth understanding before you launch. With overage disabled —
            the default — a line enforces a hard cap: as it approaches the limit the caller hears a
            warning, and at the cap the call ends. Nothing bills beyond your tier.
          </p>
          <p>
            Stripe-billed bundles stay capped at their included minutes. Overage is unavailable
            during the trial, and Stripe voice overage is not currently offered after the trial.
            Some Shopify plans support usage billing through Shopify under their own plan terms.
          </p>

          <DocsCallout variant="warning" title="Pick deliberately, not by default">
            Hard cap means a busy day can drop calls. Overage means a busy day can produce a bill
            you did not plan. Neither is wrong — but leaving it unconsidered means you find out
            which one you had during an incident.
          </DocsCallout>

          <p>
            Automatic refill is disabled for bundle trials. Review your allowance and upgrade
            through Billing or contact support if your business needs more capacity.
          </p>
        </section>

        <section id="setup" className="scroll-mt-28">
          <h2>3. Setting one up</h2>

          <DocsSteps>
            <DocsStep title="Get the bot right first">
              <p>
                Voice inherits the bot&apos;s knowledge and prompt. If the chat bot answers badly,
                the voice bot will answer badly out loud — which is considerably worse. Fix it in
                Test Bot before you provision a number.
              </p>
            </DocsStep>
            <DocsStep title="Open Voice → Phone Line">
              <p>
                In the bot sidebar, the <strong>Voice</strong> section. If no line exists yet you
                get the purchase and provisioning flow.
              </p>
            </DocsStep>
            <DocsStep title="Choose a tier and a number">
              <p>
                Pick the tier by expected call volume — you can change it later. The number is
                provisioned to the line.
              </p>
            </DocsStep>
            <DocsStep title="Work through Settings before you publish the number">
              <p>
                Greeting, voice, timezone and recording all default to something safe but generic.
                The next section covers what to change.
              </p>
            </DocsStep>
            <DocsStep title="Call it yourself">
              <p>
                From an outside phone. Listen to the greeting, ask something you know is in the
                knowledge base, then ask something that is not. Hearing your own bot is the fastest
                way to find what reads fine but sounds wrong.
              </p>
            </DocsStep>
          </DocsSteps>
        </section>

        <section id="settings" className="scroll-mt-28">
          <h2>4. Settings that matter</h2>
          <p>
            <strong>Voice → Settings</strong> is organised into Inputs, Outputs, Behavior, Booking
            and Advanced. The controls worth deliberate attention:
          </p>

          <DocsTable
            headers={['Setting', 'Why it matters']}
            rows={[
              [
                <strong key="g">Greeting</strong>,
                'The first three seconds decide whether the caller engages or asks for a human. Say who you are and what you can do — not "How may I direct your call?"',
              ],
              [
                <strong key="v">Voice</strong>,
                'Listen to the options on a phone speaker, not laptop speakers. They differ more over a phone line than they do in a browser preview.',
              ],
              [
                <strong key="t">Timezone</strong>,
                'Load-bearing for booking. Without it, "tomorrow at three" cannot be resolved, so the booking tool is not offered at all.',
              ],
              [
                <strong key="r">Recording</strong>,
                'Off by default. See section 6 before turning it on.',
              ],
            ]}
          />

          <DocsCallout variant="note" title="Callers can't skim">
            A chat visitor scans a long answer in two seconds. A caller has to listen to all of it.
            If your prompt produces thorough written answers, add an instruction to be brief and
            conversational on voice — this is usually the single biggest quality improvement
            available.
          </DocsCallout>
        </section>

        <section id="appointments" className="scroll-mt-28">
          <h2>5. Appointments</h2>
          <p>
            With booking enabled, a caller can schedule during the conversation and the appointment
            appears under <strong>Voice → Appointments</strong>.
          </p>

          <h3>Timezone is a hard prerequisite</h3>
          <p>
            Booking is deliberately withheld until a timezone is configured for the line. Converting
            a spoken local time into a stored one without knowing the zone produces appointments at
            the wrong hour — the kind of failure that is silent until someone misses a meeting.
            Chat.co would rather not offer booking than offer it wrong.
          </p>

          <h3>Service addresses</h3>
          <p>
            For businesses that travel to the customer, booking can ask for a service address. Three
            modes: never ask, ask but accept a polite decline, or require it. Salons and restaurants
            want the first; a mobile repair business wants the third.
          </p>
        </section>

        <section id="recording" className="scroll-mt-28">
          <h2>6. Recording &amp; consent</h2>
          <p>Two independent layers, and the distinction is legally significant.</p>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:speakerphone" title="Disclosure — always on">
              Every caller is told the call involves an automated assistant. This plays regardless
              of your recording setting and cannot be turned off.
            </DocsCard>
            <DocsCard icon="tabler:microphone-2" title="Audio storage — opt-in twice">
              Audio is saved only when you have enabled recording <em>and</em> the caller has given
              verbal consent during the call. Both, every time.
            </DocsCard>
          </DocsCardGrid>

          <DocsCallout variant="warning" title="Call recording law varies by jurisdiction">
            Some places require all parties to consent, some only one, and the rules differ again
            for automated systems. The consent flow above is designed to keep you on the safe side
            of the strictest common standard, but it is a product feature — not legal advice. If you
            operate across jurisdictions, check before enabling recording.
          </DocsCallout>
        </section>

        <section id="calls" className="scroll-mt-28">
          <h2>7. Reviewing calls</h2>
          <p>
            <strong>Voice → Phone Line</strong> lists call history with duration, outcome and
            minutes consumed. Opening a call shows what happened in it.
          </p>
          <p>Three things worth looking for in the first week:</p>
          <ul>
            <li>
              <strong>Calls that ended early.</strong> Usually the greeting, or a bot that talks too
              long before letting the caller speak.
            </li>
            <li>
              <strong>Questions it could not answer.</strong> Same fix as chat: add the content.
            </li>
            <li>
              <strong>Calls rejected for no minutes.</strong> A capacity signal, and a reason to
              revisit the overage decision from section 2.
            </li>
          </ul>
        </section>

        <section id="troubleshooting" className="scroll-mt-28">
          <h2>8. When something is wrong</h2>

          <DocsTable
            headers={['Symptom', 'Most likely cause']}
            rows={[
              [
                'Calls are rejected outright',
                'Minutes exhausted with overage disabled, or the subscription is past due. Check the line status before anything else.',
              ],
              [
                'Booking is never offered',
                'No timezone set on the line. The tool is withheld by design until one exists.',
              ],
              [
                'Answers are right but too long',
                'The prompt is written for reading. Add a voice-specific brevity instruction.',
              ],
              [
                'The bot does not know something it should',
                'A knowledge problem, not a voice problem. Reproduce it in Test Bot — if it fails there too, fix the knowledge base.',
              ],
            ]}
          />

          <p>
            Anything the bot gets wrong on the phone it will also get wrong in chat. Reproducing in{' '}
            <strong>Test Bot</strong> first tells you which half of the system to debug, and saves
            you from tuning voice settings to fix a missing document.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default VoiceLinesPage;
